"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { addDays, addMonths, format, startOfDay, subDays } from "date-fns";
import DatePickerCustom from "@/components/ui/DatePickerCustom";
import { getDateFnsLocale } from "@/lib/date-fns-locale";
import type { PartialPaymentPolicy } from "@/types/partial-payment";
import {
  computeScheduleLateFees,
  getDueDateWindowForSplitCount,
  getLateFeePercentForSplitCount,
  isDueDateWithinWindow,
  type DueDateWindow,
} from "@/lib/partial-payment-schedule";

export interface PaymentScheduleRow {
  amount: string;
  due_date: string;
}

interface PaymentSchedulePlannerProps {
  balanceAmount: number;
  policy: PartialPaymentPolicy;
  currencySymbol: string;
  value: PaymentScheduleRow[];
  onChange: (rows: PaymentScheduleRow[]) => void;
  t: (key: string, fallback?: string) => string;
  locale?: string;
  /** Tur kalkış tarihi (yyyy-MM-dd) – vade üst sınırı için */
  departureDate?: string | null;
}

const FORM_LABEL = "block text-xs font-semibold text-gray-700 mb-1";
const DATE_BUTTON =
  "min-h-[48px] py-2.5 px-3 sm:px-4 text-sm [&_span]:text-sm sm:[&_span]:text-base";

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** yyyy-MM-dd → yerel gün (UTC kayması yok) */
function parseLocalYmd(s: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s.trim());
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? undefined : startOfDay(d);
}

function formatLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

/** Son vade: kalkış − N gün (kalkış günü değil) */
function lastAllowedDueDate(
  departureDate: string | null | undefined,
  fullPaymentDaysBefore?: number | null
): Date | undefined {
  if (!departureDate) return undefined;
  const dep = parseLocalYmd(departureDate);
  if (!dep) return undefined;
  const daysBefore = Math.max(0, fullPaymentDaysBefore ?? 30);
  return subDays(dep, daysBefore);
}

/** İlk planlı vade: bugünden 1 ay sonra (aynı gün); en erken bugün + 7 gün */
function firstDueAnchor(today: Date): Date {
  const earliest = addDays(today, 7);
  const nextMonthSameDay = startOfDay(addMonths(today, 1));
  return nextMonthSameDay.getTime() >= earliest.getTime() ? nextMonthSameDay : startOfDay(earliest);
}

function clampDueBetween(d: Date, min: Date, max: Date): Date {
  let x = startOfDay(d);
  if (x.getTime() < min.getTime()) x = min;
  if (x.getTime() > max.getTime()) x = max;
  return x;
}

/** Taksit vadeleri: ilk vadeden itibaren her ay aynı gün; son vade üst sınırını aşmaz */
function defaultDueDates(
  count: number,
  departureDate?: string | null,
  fullPaymentDaysBefore?: number | null,
  policyWindow?: DueDateWindow | null,
): string[] {
  if (count <= 0) return [];

  const today = startOfDay(new Date());
  let minDue = addDays(today, 1);
  if (policyWindow) {
    const wStart = parseLocalYmd(policyWindow.start);
    if (wStart && wStart.getTime() > minDue.getTime()) {
      minDue = wStart;
    }
  }
  const latest = lastAllowedDueDate(departureDate, fullPaymentDaysBefore);
  let maxBound =
    latest && latest.getTime() >= today.getTime() ? latest : startOfDay(addMonths(today, 24));
  if (policyWindow) {
    const wEnd = parseLocalYmd(policyWindow.end);
    if (wEnd && wEnd.getTime() < maxBound.getTime()) {
      maxBound = wEnd;
    }
  }
  if (minDue.getTime() > maxBound.getTime()) {
    return Array.from({ length: count }, () => formatLocalYmd(maxBound));
  }

  if (count === 2 && minDue.getTime() < maxBound.getTime()) {
    return [formatLocalYmd(minDue), formatLocalYmd(maxBound)];
  }

  let anchor = firstDueAnchor(today);
  if (count > 1) {
    const lastFromAnchor = addMonths(anchor, count - 1);
    if (lastFromAnchor.getTime() > maxBound.getTime()) {
      anchor = addMonths(maxBound, -(count - 1));
    }
  }
  if (anchor.getTime() < minDue.getTime()) {
    anchor = minDue;
  }

  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    let due = addMonths(anchor, i);
    due = clampDueBetween(due, minDue, maxBound);
    if (i > 0) {
      const prev = parseLocalYmd(out[i - 1]);
      if (prev && due.getTime() <= prev.getTime()) {
        due = clampDueBetween(addMonths(prev, 1), minDue, maxBound);
      }
    }
    out.push(formatLocalYmd(due));
  }
  return out;
}

function datesFromMonthlyAnchor(anchor: Date, count: number, maxBound: Date, minDue: Date): string[] {
  const base = startOfDay(anchor);
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    let due = addMonths(base, i);
    due = clampDueBetween(due, minDue, maxBound);
    if (i > 0) {
      const prev = parseLocalYmd(out[i - 1]);
      if (prev && due.getTime() <= prev.getTime()) {
        due = clampDueBetween(addMonths(prev, 1), minDue, maxBound);
      }
    }
    out.push(formatLocalYmd(due));
  }
  return out;
}

export default function PaymentSchedulePlanner({
  balanceAmount,
  policy,
  currencySymbol,
  value,
  onChange,
  t,
  locale = "en",
  departureDate = null,
}: PaymentSchedulePlannerProps) {
  const maxSplits = Math.max(1, policy.max_balance_splits ?? 3);
  const [splitCount, setSplitCount] = useState(Math.min(maxSplits, Math.max(1, value.length || 1)));
  const prevBalanceRef = useRef<number | null>(null);

  const lateFeePercent = useMemo(
    () => getLateFeePercentForSplitCount(policy, splitCount),
    [policy, splitCount],
  );

  const policyWindow = useMemo(
    () => getDueDateWindowForSplitCount(policy, splitCount),
    [policy, splitCount],
  );

  const maxDueDate = useMemo(() => {
    const today = startOfDay(new Date());
    let max: Date | undefined;
    const latest = lastAllowedDueDate(departureDate, policy.full_payment_days_before_departure);
    if (latest && latest.getTime() >= today.getTime()) {
      max = latest;
    }
    if (policyWindow) {
      const wEnd = parseLocalYmd(policyWindow.end);
      if (wEnd) {
        max = max && max.getTime() < wEnd.getTime() ? max : wEnd;
      }
    }
    return max;
  }, [departureDate, policy.full_payment_days_before_departure, policyWindow]);

  const minDueDate = useMemo(() => {
    const tomorrow = addDays(startOfDay(new Date()), 1);
    if (!policyWindow) return tomorrow;
    const wStart = parseLocalYmd(policyWindow.start);
    if (wStart && wStart.getTime() > tomorrow.getTime()) return wStart;
    return tomorrow;
  }, [policyWindow]);

  const dateFnsLocale = useMemo(() => getDateFnsLocale(locale), [locale]);

  const policyWindowHint = useMemo(() => {
    if (!policyWindow) return null;
    const startStr = format(parseLocalYmd(policyWindow.start) ?? new Date(), "d MMMM yyyy", {
      locale: dateFnsLocale,
    });
    const endStr = format(parseLocalYmd(policyWindow.end) ?? new Date(), "d MMMM yyyy", {
      locale: dateFnsLocale,
    });
    if (locale === "tr") {
      return `${splitCount} vade için izin verilen tarih aralığı: ${startStr} – ${endStr}.`;
    }
    return `Allowed due dates for ${splitCount} installment(s): ${startStr} – ${endStr}.`;
  }, [policyWindow, splitCount, locale, dateFnsLocale]);

  const deadlineHint = useMemo(() => {
    if (!maxDueDate || !departureDate) return null;
    const dep = parseLocalYmd(departureDate);
    if (!dep) return null;
    const daysBefore = policy.full_payment_days_before_departure ?? 30;
    const lastStr = format(maxDueDate, "d MMMM yyyy", { locale: dateFnsLocale });
    const depStr = format(dep, "d MMMM yyyy", { locale: dateFnsLocale });
    if (locale === "tr") {
      return `Son taksit en geç ${lastStr} (${depStr} kalkıştan ${daysBefore} gün önce). Kalkış günü vade olamaz.`;
    }
    return `Last due by ${lastStr} (${daysBefore} days before departure on ${depStr}). Departure day cannot be a due date.`;
  }, [maxDueDate, departureDate, policy.full_payment_days_before_departure, locale, dateFnsLocale]);

  const dateDisabled = useMemo(() => {
    if (maxDueDate) {
      return { before: minDueDate, after: maxDueDate };
    }
    return { before: minDueDate };
  }, [maxDueDate, minDueDate]);

  const calendarStartMonth = useMemo(() => {
    if (!maxDueDate) return minDueDate;
    return minDueDate.getTime() <= maxDueDate.getTime() ? minDueDate : maxDueDate;
  }, [minDueDate, maxDueDate]);

  const calendarEndMonth = useMemo(() => {
    if (!maxDueDate) return addMonths(minDueDate, 12);
    return minDueDate.getTime() <= maxDueDate.getTime() ? maxDueDate : minDueDate;
  }, [minDueDate, maxDueDate]);

  const redistribute = useCallback(
    (count: number, preserveDates = true) => {
      if (balanceAmount <= 0) {
        onChange([]);
        return;
      }
      const suggested = defaultDueDates(
        count,
        departureDate,
        policy.full_payment_days_before_departure,
        getDueDateWindowForSplitCount(policy, count),
      );
      const base = round2(balanceAmount / count);
      const rows: PaymentScheduleRow[] = [];
      let assigned = 0;
      for (let i = 0; i < count; i++) {
        const isLast = i === count - 1;
        const amt = isLast ? round2(balanceAmount - assigned) : base;
        assigned += amt;
        const prevDate = preserveDates ? value[i]?.due_date : "";
        rows.push({
          amount: amt.toFixed(2),
          due_date: prevDate && prevDate.trim() !== "" ? prevDate : suggested[i] ?? "",
        });
      }
      onChange(rows);
    },
    [balanceAmount, onChange, value, departureDate, policy]
  );

  useEffect(() => {
    if (balanceAmount <= 0) {
      prevBalanceRef.current = balanceAmount;
      return;
    }
    if (value.length === 0) {
      redistribute(splitCount, false);
      prevBalanceRef.current = balanceAmount;
      return;
    }

    const prevBalance = prevBalanceRef.current;
    if (prevBalance !== null && Math.abs(prevBalance - balanceAmount) > 0.02) {
      redistribute(splitCount, true);
      prevBalanceRef.current = balanceAmount;
      return;
    }

    prevBalanceRef.current = balanceAmount;
  }, [balanceAmount, splitCount, value.length, redistribute]);

  const applyDueDateAtIndex = useCallback(
    (index: number, picked: Date | undefined) => {
      if (!picked) {
        const next = [...value];
        next[index] = { ...next[index], due_date: "" };
        onChange(next);
        return;
      }
      const maxBound = maxDueDate ?? addMonths(startOfDay(new Date()), 24);
      const base = startOfDay(picked);
      const next = [...value];
      if (index === 0 && next.length > 1) {
        const dates = datesFromMonthlyAnchor(base, next.length, maxBound, minDueDate);
        for (let i = 0; i < next.length; i++) {
          next[i] = { ...next[i], due_date: dates[i] ?? "" };
        }
      } else {
        next[index] = { ...next[index], due_date: formatLocalYmd(clampDueBetween(base, minDueDate, maxBound)) };
      }
      onChange(next);
    },
    [value, onChange, maxDueDate, minDueDate],
  );

  const sumAmounts = value.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const sumMismatch = balanceAmount > 0 && Math.abs(sumAmounts - balanceAmount) > 0.02;
  const loc = locale === "tr" ? "tr-TR" : "en-GB";

  const scheduleFeeSummary = useMemo(
    () => computeScheduleLateFees(policy, value),
    [policy, value],
  );
  const totalFees = scheduleFeeSummary.feesTotal;

  const formatMoney = (n: number) =>
    n.toLocaleString(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="rounded-xl border border-amber-200/80 bg-gradient-to-b from-amber-50/90 to-white p-4 sm:p-5 space-y-5 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900">
            {t("payment.schedule.title", "Payment schedule")}
          </h3>
          <p className="text-xs text-gray-600 mt-1 max-w-xl leading-relaxed">
            {t(
              "payment.schedule.hint",
              "Plan the remaining balance by due date. This is not bank installment (POS)."
            )}
          </p>
          {policyWindowHint && (
            <p className="text-xs font-medium text-emerald-900/90 mt-2 leading-relaxed">{policyWindowHint}</p>
          )}
          {deadlineHint && (
            <p className="text-xs text-amber-900/80 mt-2 leading-relaxed">{deadlineHint}</p>
          )}
        </div>
        {lateFeePercent > 0 && (
          <span className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-900">
            {t("payment.schedule.late_fee_preview", "Late fee")}: %{lateFeePercent.toFixed(2)}{" "}
            {t("payment.schedule.per_due", "per due")}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-amber-100/80 bg-white/70 px-4 py-3">
        <div>
          <span className="block text-xs font-medium text-gray-600 mb-0.5">
            {t("payment.schedule.remaining", "Remaining balance")}
          </span>
          <div className="text-lg font-bold text-gray-900 tabular-nums">
            {formatMoney(balanceAmount)} {currencySymbol}
          </div>
        </div>
        <div className="min-w-[120px]">
          <label className={FORM_LABEL} htmlFor="payment-schedule-split-count">
            {t("payment.schedule.installment_count", "Number of dues")}
          </label>
          <select
            id="payment-schedule-split-count"
            className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm bg-white focus:border-alsero-first focus:ring-2 focus:ring-alsero-first/20"
            value={splitCount}
            onChange={(e) => {
              const n = Math.min(maxSplits, Math.max(1, parseInt(e.target.value, 10) || 1));
              setSplitCount(n);
              redistribute(n, false);
            }}
          >
            {Array.from({ length: maxSplits }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-3 pb-1">
        {value.map((row, index) => {
          const amt = parseFloat(row.amount) || 0;
          const fee = round2(amt * (lateFeePercent / 100));
          const dueLabel = `${t("payment.schedule.due_n", "Due")} #${index + 1}`;
          const selectedDate = parseLocalYmd(row.due_date);
          const isLastDue = index === value.length - 1;

          return (
            <div
              key={index}
              className="rounded-xl border border-gray-200/90 bg-white p-4 shadow-sm space-y-3"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold uppercase tracking-wide text-alsero-first">
                  {dueLabel}
                </span>
                {lateFeePercent > 0 && amt > 0 && (
                  <span className="text-xs text-gray-600 tabular-nums">
                    {t("payment.schedule.fee", "fee")}: +{formatMoney(fee)} {currencySymbol} →{" "}
                    <span className="font-semibold text-gray-900">
                      {formatMoney(amt + fee)} {currencySymbol}
                    </span>
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <DatePickerCustom
                  key={`schedule-due-picker-${index}-${splitCount}`}
                  inputId={`payment-schedule-due-date-${index}`}
                  label={
                    isLastDue && value.length > 1
                      ? t(
                          "payment.schedule.due_date_final",
                          locale === "tr" ? "Vade tarihi (son taksit)" : "Due date (final installment)",
                        )
                      : t("payment.schedule.due_date", locale === "tr" ? "Vade tarihi" : "Due date")
                  }
                  labelClassName={FORM_LABEL}
                  buttonClassName={DATE_BUTTON}
                  selected={selectedDate}
                  language={locale}
                  disabled={dateDisabled}
                  defaultMonth={selectedDate ?? maxDueDate ?? minDueDate}
                  startMonth={calendarStartMonth}
                  endMonth={calendarEndMonth}
                  onSelect={(d) => applyDueDateAtIndex(index, d)}
                />
                <div>
                  <label className={FORM_LABEL} htmlFor={`schedule-amount-${index}`}>
                    {t(
                      "payment.schedule.amount_principal",
                      locale === "tr"
                        ? "Ana para (gecikme bedeli hariç)"
                        : "Principal (excludes late fee)",
                    )}{" "}
                    ({currencySymbol})
                  </label>
                  <input
                    id={`schedule-amount-${index}`}
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={row.amount}
                    onChange={(e) => {
                      const next = [...value];
                      next[index] = { ...next[index], amount: e.target.value };
                      onChange(next);
                    }}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm tabular-nums focus:border-alsero-first focus:ring-2 focus:ring-alsero-first/20 min-h-[48px]"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50/80 px-4 py-3 text-xs text-gray-700">
        <span>
          {t("payment.schedule.planned_total", locale === "tr" ? "Planlanan ana para" : "Planned principal")}:{" "}
          <strong className="tabular-nums">{formatMoney(sumAmounts)} {currencySymbol}</strong>
        </span>
        {lateFeePercent > 0 && (
          <span>
            {t("payment.schedule.total_fees", locale === "tr" ? "Tahmini gecikme bedeli" : "Est. late fees")}:{" "}
            <strong className="tabular-nums">{formatMoney(totalFees)} {currencySymbol}</strong>
          </span>
        )}
      </div>

      {sumMismatch && (
        <div className="space-y-2" role="alert">
          <p className="text-xs text-red-600 font-medium">
            {t(
              "payment.schedule.sum_error_detail",
              locale === "tr"
                ? `Vade ana paralarının toplamı kalan bakiyeye eşit olmalı (${formatMoney(balanceAmount)} ${currencySymbol}). Gecikme bedeli bu alana eklenmez; vade satırında ayrı gösterilir.`
                : `Due principal amounts must total the remaining balance (${formatMoney(balanceAmount)} ${currencySymbol}). Late fees are not included here; they are shown separately on each due.`,
            )}
          </p>
          <button
            type="button"
            className="text-xs font-semibold text-alsero-first hover:underline"
            onClick={() => redistribute(splitCount, true)}
          >
            {t(
              "payment.schedule.fix_amounts",
              locale === "tr" ? "Tutarları kalan bakiyeye göre yeniden dağıt" : "Redistribute amounts to match balance",
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export function validatePaymentScheduleRows(
  balanceAmount: number,
  rows: PaymentScheduleRow[],
  t: (key: string, fallback?: string) => string,
  policy?: PartialPaymentPolicy | null,
): string | null {
  if (balanceAmount <= 0.009) return null;
  if (!rows.length) return t("payment.schedule.required", "Define a payment schedule for the remaining balance.");
  const window =
    policy && rows.length > 0 ? getDueDateWindowForSplitCount(policy, rows.length) : null;
  let sum = 0;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const amt = Math.round((parseFloat(row.amount) || 0) * 100) / 100;
    if (!row.due_date) return t("payment.schedule.date_required", "Each due needs a date.");
    if (window && !isDueDateWithinWindow(row.due_date, window)) {
      return t(
        "payment.schedule.due_outside_window",
        `Due #${i + 1} must be between ${window.start} and ${window.end}.`,
      );
    }
    if (Number.isNaN(amt) || amt <= 0) return t("payment.schedule.amount_invalid", "Enter valid due amounts.");
    sum += amt;
  }
  sum = Math.round(sum * 100) / 100;
  if (Math.abs(sum - balanceAmount) > 0.02) {
    return t(
      "payment.schedule.sum_error",
      `Installment principal must total ${balanceAmount.toFixed(2)} (late fees excluded).`,
    );
  }
  return null;
}
